import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { buttonVariants } from '@systemzero-inc/ui/primitives/button';
import { cn } from '~/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  footer?: React.ReactNode;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  footer,
  captionLayout = 'label',
  ...props
}: CalendarProps) {
  const useDropdownCaption = captionLayout !== 'label';

  return (
    <div className="flex flex-col">
      <DayPicker
        showOutsideDays={showOutsideDays}
        captionLayout={captionLayout}
        className={cn('p-3', className)}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-6 sm:space-y-0',
          month: 'space-y-4',
          month_caption: cn(
            'flex justify-center pt-1 relative items-center px-8',
            useDropdownCaption && 'flex-wrap gap-2',
          ),

          caption_label: cn(
            'text-sm font-medium',
            useDropdownCaption &&
              'flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm pointer-events-none [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
          ),
          dropdowns: 'flex w-full items-center justify-center gap-2',
          dropdown_root: cn(
            'relative flex h-9 shrink-0 items-center rounded-md border border-input bg-background px-2 text-sm shadow-sm',
            'focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/50',
          ),
          dropdown: 'absolute inset-0 h-full w-full cursor-pointer opacity-0',
          chevron: 'size-4 text-muted-foreground',
          nav: 'absolute left-4 right-4 z-10 m-0',
          button_previous: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-0',
          ),
          button_next: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-0',
          ),
          month_grid: 'w-full border-collapse space-y-1',
          weekdays: 'flex',
          weekday: 'text-muted-foreground w-9 font-normal text-[0.8rem]',
          week: 'flex w-full mt-2',
          day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 focus-within:relative focus-within:z-20',
          day_button: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-9 p-0 font-normal aria-selected:opacity-100 cursor-pointer',
          ),
          range_start: 'rounded-md [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground',
          range_end: 'rounded-md [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground',
          selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground [&:first-child]:rounded-l-md [&:last-child]:rounded-r-md',
          today: 'bg-accent text-accent-foreground',
          outside: 'outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          disabled: 'text-muted-foreground opacity-50',
          range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
          hidden: 'invisible',
          ...classNames,
        }}
        components={{
          Chevron: (props) => {
            if (props.orientation === 'left') {
              return <ChevronLeft className={cn('size-4', props.className)} {...props} />;
            }
            if (props.orientation === 'down') {
              return <ChevronDown className={cn('size-4', props.className)} {...props} />;
            }
            return <ChevronRight className={cn('size-4', props.className)} {...props} />;
          },
        }}
        {...props}
      />
      {footer && <div className="flex items-center justify-end gap-2.5 px-3 pb-3">{footer}</div>}
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
